import { useCallback, useEffect, useState } from 'react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import {
  createPrivacyCategory,
  createPrivacyGroup,
  deletePrivacyCategory,
  deletePrivacyGroup,
  ensureDefaultPrivacyGroups,
  listPrivacyCategories,
  listPrivacyGroups,
  updatePrivacyGroup,
} from '../services/categoryService';

function Settings() {
  const { currentUser } = useAuth();
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [name, setName] = useState('Family');
  const [color, setColor] = useState('#8a5a34');
  const [groupName, setGroupName] = useState('Family');
  const [groupColor, setGroupColor] = useState('#8a5a34');
  const [groupMembers, setGroupMembers] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadCategories = useCallback(async () => {
    if (!currentUser) return;
    setError('');
    try {
      await ensureDefaultPrivacyGroups(currentUser.uid);
      const [loadedCategories, loadedGroups] = await Promise.all([
        listPrivacyCategories(currentUser.uid),
        listPrivacyGroups(currentUser.uid),
      ]);
      setCategories(loadedCategories);
      setGroups(loadedGroups);
    } catch (err) {
      setError(err.message);
    }
  }, [currentUser]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setMessage('');
    try {
      if (!name.trim()) throw new Error('Category name is required.');
      await createPrivacyCategory(currentUser.uid, { name, color });
      setName('');
      setMessage('Category saved.');
      await loadCategories();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleGroupSubmit(event) {
    event.preventDefault();
    setError('');
    setMessage('');
    try {
      if (!groupName.trim()) throw new Error('Group name is required.');
      await createPrivacyGroup(currentUser.uid, {
        name: groupName,
        color: groupColor,
        memberIds: groupMembers.split(/[\s,]+/),
      });
      setGroupName('');
      setGroupMembers('');
      setMessage('Privacy group saved.');
      await loadCategories();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleGroupMembers(group, value) {
    setError('');
    setMessage('');
    try {
      await updatePrivacyGroup(currentUser.uid, group.id, {
        ...group,
        memberIds: value.split(/[\s,]+/),
      });
      setMessage('Group members updated.');
      await loadCategories();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(categoryId) {
    setError('');
    setMessage('');
    try {
      await deletePrivacyCategory(currentUser.uid, categoryId);
      setMessage('Category deleted.');
      await loadCategories();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDeleteGroup(groupId) {
    setError('');
    setMessage('');
    try {
      await deletePrivacyGroup(currentUser.uid, groupId);
      setMessage('Privacy group deleted.');
      await loadCategories();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <>
      <SEO title="Settings" />
      <section className="page-heading">
        <p className="eyebrow">Settings</p>
        <h1>Prepare reusable privacy groups.</h1>
        <p>Family is a default custom group. Add member UIDs to make group visibility enforceable.</p>
      </section>
      {error && <p className="error-text">{error}</p>}
      {message && <p className="notice">{message}</p>}
      <section className="editor-layout">
        <form className="surface-card stacked-form" onSubmit={handleGroupSubmit}>
          <h2>Privacy groups</h2>
          <label>Name<input value={groupName} onChange={(event) => setGroupName(event.target.value)} placeholder="Family, cousins, writing group" /></label>
          <label>Color<input type="color" value={groupColor} onChange={(event) => setGroupColor(event.target.value)} /></label>
          <label>Member UIDs<textarea rows="4" value={groupMembers} onChange={(event) => setGroupMembers(event.target.value)} placeholder="Paste Firebase user IDs separated by spaces or commas" /></label>
          <button className="button" type="submit">Add group</button>
        </form>
        <div className="surface-card">
          <h2>Saved groups</h2>
          <div className="item-list">
            {groups.map((group) => (
              <article className="content-item category-item" key={group.id} style={{ '--friend-color': group.color }}>
                <p className="friend-label"><span />{group.name}</p>
                <label>Member UIDs<textarea rows="3" defaultValue={(group.memberIds || []).join('\n')} onBlur={(event) => handleGroupMembers(group, event.target.value)} /></label>
                <button className="button button-secondary button-small" type="button" onClick={() => handleDeleteGroup(group.id)}>Delete</button>
              </article>
            ))}
          </div>
        </div>
        <form className="surface-card stacked-form" onSubmit={handleSubmit}>
          <h2>My categories</h2>
          <label>Name<input value={name} onChange={(event) => setName(event.target.value)} placeholder="Family, cousins, writing group" /></label>
          <label>Color<input type="color" value={color} onChange={(event) => setColor(event.target.value)} /></label>
          <button className="button" type="submit">Add category</button>
        </form>
        <div className="surface-card">
          <h2>Saved categories</h2>
          <div className="item-list">
            {categories.map((category) => (
              <article className="content-item category-item" key={category.id} style={{ '--friend-color': category.color }}>
                <p className="friend-label"><span />{category.name}</p>
                <button className="button button-secondary button-small" type="button" onClick={() => handleDelete(category.id)}>Delete</button>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Settings;
